// Maps Firebase Auth error codes to human-friendly messages so the raw
// "Firebase: Error (auth/...)" string is never shown to the user.

// Codes that mean the user intentionally dismissed/cancelled the popup.
// Callers can treat these as "no error" rather than showing a message.
export const POPUP_CANCEL_CODES = [
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
];

const FRIENDLY_MESSAGES: Record<string, string> = {
  // Popup / OAuth
  "auth/popup-closed-by-user": "Sign-in was cancelled. Please try again.",
  "auth/cancelled-popup-request": "Sign-in was cancelled. Please try again.",
  "auth/user-cancelled": "Sign-in was cancelled. Please try again.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in popup. Please allow popups and try again.",
  "auth/account-exists-with-different-credential":
    "An account already exists with this email using a different sign-in method.",
  "auth/operation-not-allowed":
    "This sign-in method is not enabled. Please contact support.",
  "auth/unauthorized-domain":
    "This domain isn't authorized for sign-in. Please contact support.",

  // Email / password
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-disabled": "This account has been disabled. Please contact support.",
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Incorrect email or password. Please try again.",
  "auth/invalid-credential": "Incorrect email or password. Please try again.",
  "auth/invalid-login-credentials":
    "Incorrect email or password. Please try again.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/weak-password": "Password is too weak. Please choose a stronger one.",

  // Network / rate limiting
  "auth/network-request-failed":
    "Network error. Please check your connection and try again.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a moment and try again.",
  "auth/internal-error": "Something went wrong. Please try again.",
};

/** True when the error is just the user closing/cancelling the popup. */
export const isPopupCancel = (error: any): boolean =>
  !!error?.code && POPUP_CANCEL_CODES.includes(error.code);

// Codes meaning the entered credentials themselves were rejected — i.e. an
// actual failed login attempt, as opposed to a network error or the popup
// being cancelled. These are what the backend's 5-attempt lockout counts.
const LOGIN_FAILURE_CODES = [
  "auth/wrong-password",
  "auth/user-not-found",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
];

/** True when Firebase rejected the email/password themselves (not a network/other error). */
export const isLoginFailureCode = (error: any): boolean =>
  !!error?.code && LOGIN_FAILURE_CODES.includes(error.code);

/**
 * Returns a human-friendly message for a Firebase Auth error, never the raw
 * "Firebase: Error (...)" string.
 */
export const getFirebaseAuthErrorMessage = (
  error: any,
  fallback = "Something went wrong. Please try again.",
): string => {
  if (error?.code && FRIENDLY_MESSAGES[error.code]) {
    return FRIENDLY_MESSAGES[error.code];
  }
  return fallback;
};
